import { FC, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import type { AnyAction } from '@reduxjs/toolkit';
import type { AppState, AppDispatch } from '@nextgen-web-framework/core';
import {
  useTranslation,
  TextLink,
  TextLinkStyle,
  Theme,
  Drawer,
  Button,
  ButtonTypes,
  ButtonStyles,
  ButtonSize,
  CardRadioButton,
  MLPVersion,
  SmartInput,
  InputType,
  InputValidations,
  DropDown,
  ButtonIconPosition,
  IconAnimationClassName,
} from '@nextgen-web-framework/all';
import {
  CheckoutResponse,
  ErrorResponse,
  getTaxDocumentsSliceReq,
  setPlaceOrderDisabled,
  TaxDocument,
  updateCheckoutSliceReq,
  setTaxContactContainerState,
  CheckoutStatus,
} from '@commerce-checkout-webcomponents/sdk';
import { isValidEmail, stringBooleanUndefinedChecksOutput } from '../common/transformer';
import { ContainerStateEnum } from '../common/types/CheckoutType';
import CheckoutInlineText from '../checkout-inline-text/CheckoutInlineText';
import styles from './CheckoutTaxAndContact.module.scss';

export const CheckoutTaxAndContact: FC = () => {
  const t = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [showTaxContactDrawer, setShowTaxContactDrawer] = useState(false);
  const checkout: CheckoutResponse = useSelector((state: AppState) => state?.commerceCheckout?.checkout || {});
  const [checkedDocument, setCheckedDocument] = useState(checkout?.taxDocument);
  const [email, setEmail] = useState(checkout?.user?.email);
  const [emailValidationStatus, setEmailValidationStatus] = useState(InputValidations.DEFAULT);
  const [enableApiCall, setEnableApiCall] = useState(false);
  const [containerState, setContainerState] = useState<ContainerStateEnum>(ContainerStateEnum.FILLED);
  const taxDocuments: Array<TaxDocument> = useSelector((state: AppState) => state?.commerceCheckout?.taxDocuments);
  const emailRegex: RegExp = useSelector((state: AppState) => state?.commerceCheckout?.emailRegex);
  const [taxDocumentsData, setTaxDocumentsData] = useState<TaxDocument[]>(taxDocuments);

  useEffect(() => {
    checkout?.allowTaxPayerUpdate &&
      dispatch(getTaxDocumentsSliceReq({ checkoutId: checkout?.checkoutId }) as unknown as AnyAction);
  }, []);

  const isChecked = (taxDocument: TaxDocument): boolean =>
    checkedDocument?.taxPayerGivenName == taxDocument?.taxPayerGivenName;

  useEffect(() => {
    if (taxDocuments?.length) {
      if (taxDocuments.length > 1) {
        const sortedTaxDocuments: TaxDocument[] = [];
        taxDocuments.forEach((taxDocument: TaxDocument) => {
          if (isChecked(taxDocument)) {
            sortedTaxDocuments.unshift(taxDocument);
          } else {
            sortedTaxDocuments.push(taxDocument);
          }
        });
        setTaxDocumentsData(sortedTaxDocuments);
      } else {
        setTaxDocumentsData(taxDocuments);
      }
    }
  }, [taxDocuments, checkedDocument]);

  useEffect(() => {
    if (checkout) {
      const status = !email
        ? InputValidations.DEFAULT
        : isValidEmail(email, emailRegex)
          ? InputValidations.VALID
          : InputValidations.INVALID;
      setEmailValidationStatus(status);
    }
  }, [email]);

  const taxDetailLoading: boolean = useSelector(
    (state: AppState) => state?.commerceCheckout?.loadingState?.taxDetailLoading?.isLoading || false,
  );

  const onChangeEmail = (email?: string) => {
    setEmail(email);
    setEnableApiCall(true);
  };

  const changeTaxDetails = () => {
    setContainerState(ContainerStateEnum.EXPANDED);
    dispatch(setPlaceOrderDisabled(true));
    dispatch(setTaxContactContainerState(ContainerStateEnum.EXPANDED) as unknown as AnyAction);
  };
  const confirmTaxDocument = () => {
    dispatch(setPlaceOrderDisabled(false));
    dispatch(setTaxContactContainerState(ContainerStateEnum.FILLED) as unknown as AnyAction);
    enableApiCall
      ? dispatch(
          updateCheckoutSliceReq({
            checkoutId: checkout?.checkoutId,
            payload: {
              taxDocument: checkedDocument,
              version: checkout?.version,
              user: { ...checkout.user, email: email },
            },
          }) as unknown as AnyAction,
        ).then((response: CheckoutResponse & ErrorResponse) => {
          if (!response?.code) {
            setContainerState(ContainerStateEnum.FILLED);
          }
        })
      : setContainerState(ContainerStateEnum.FILLED);
  };

  return (
    <div className={`${styles['checkout-block-container']}`}>
      <div data-testid="checkout-tax-contact" className={`${styles['checkout-tax-contact']} `}>
        <div className={styles['checkout-tax-header']}>
          <div className={styles['checkout-tax-header-txt']} data-testid="checkout-tax-header-txt">
            {t('checkout.CheckoutTaxAndContact.headingText')}
          </div>
          {stringBooleanUndefinedChecksOutput([
            containerState === ContainerStateEnum.FILLED,
            checkout?.status != CheckoutStatus.OrderCreated,
          ]) && (
            <TextLink
              data-testid="checkout-tax-header-btn"
              text={t('checkout.CheckoutTaxAndContact.changeButtonText')}
              onClick={() => changeTaxDetails()}
              textLinkStyle={TextLinkStyle.UNDERLINE}
              theme={Theme.PURPLE}
              scroll={false}
            />
          )}
        </div>
        {containerState == ContainerStateEnum.FILLED && (
          <div className={styles['checkout-tax-detail']} data-testid="checkout-tax-detail-filled-state">
            <div className={styles['checkout-tax-detail-sub-section']}>
              <CheckoutInlineText
                text={t('checkout.CheckoutTaxAndContact.taxPayerText')}
                customClass={styles['checkout-tax-payer-field-label']}
              />
              <CheckoutInlineText
                text={checkout?.taxDocument?.taxPayerGivenName ?? ''}
                customClass={styles['checkout-tax-payer-field-value']}
              />
            </div>
            <div className={styles['checkout-tax-detail-sub-section']}>
              <CheckoutInlineText
                text={t('checkout.CheckoutTaxAndContact.taxInvoiceEmailText')}
                customClass={styles['checkout-tax-payer-field-label']}
              />
              <CheckoutInlineText text={email ?? ''} customClass={styles['checkout-tax-payer-field-value']} />
            </div>
          </div>
        )}
        {containerState == ContainerStateEnum.EXPANDED && (
          <div
            className={styles['checkout-tax-detail-expanded-state']}
            data-testid="checkout-tax-detail-expanded-state"
          >
            <div className={styles['tax-dropdown']}>
              <DropDown
                name={'tax-documents'}
                dropdownLabel={t('checkout.CheckoutTaxAndContact.taxPayerText')}
                value={checkedDocument?.taxPayerGivenName}
                mlpVersion={MLPVersion.TWO}
                onClick={(e) => {
                  e?.preventDefault();
                  setShowTaxContactDrawer(true);
                }}
                disabled={!checkout?.allowTaxPayerUpdate || !(taxDocumentsData?.length > 1)}
              />
            </div>
            <div className={styles['email-field']}>
              <SmartInput
                name="smartInput-element"
                smartInputType={InputType.EMAIL}
                inputValue={email}
                onChange={(e) => onChangeEmail(e.target.value)}
                status={emailValidationStatus}
                emailLabel={t('checkout.CheckoutTaxAndContact.taxInvoiceEmailInput')}
              />
            </div>
            <div className={styles['checkout-tax-confirm-btn']}>
              <Button
                title={!taxDetailLoading ? t('checkout.CheckoutTaxAndContact.confirmCta') : undefined}
                data-testid="checkout-tax-contact-confirm-cta"
                type={ButtonTypes.BUTTON}
                buttonStyle={ButtonStyles.SOLID}
                size={ButtonSize.MEDIUM}
                onClick={confirmTaxDocument}
                disabled={emailValidationStatus == InputValidations.INVALID || taxDetailLoading || showTaxContactDrawer}
                {...(taxDetailLoading && {
                  iconPosition: ButtonIconPosition.LEFT,
                  icon: {
                    iconName: 'spinner-third',
                    iconAnimationClassName: IconAnimationClassName.SPIN,
                  },
                })}
              />
            </div>
          </div>
        )}
        {showTaxContactDrawer && (
          <div className={styles['checkout-tax-contact-drawer']}>
            <Drawer
              header={<h4 className={styles['drawer-title']}>{t('checkout.CheckoutTaxAndContact.drawerTitle')}</h4>}
              closeIcon={true}
              onClose={() => setShowTaxContactDrawer(false)}
              bottomGap={0}
            >
              <div className={styles['checkout-drawer-invoice-names']}>
                {taxDocumentsData?.map((taxDocument: TaxDocument) => (
                  <CardRadioButton
                    mlpVersion={MLPVersion.ONE}
                    label={taxDocument.taxPayerGivenName}
                    checked={isChecked(taxDocument)}
                    key={taxDocument.taxId}
                    className={`${styles['invoice-names-option']} ${styles['selected']}`}
                    theme={Theme.PURPLE}
                    onClick={() => {
                      setCheckedDocument(taxDocument);
                      setEnableApiCall(true);
                      setShowTaxContactDrawer(false);
                    }}
                  />
                ))}
              </div>
            </Drawer>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckoutTaxAndContact;
